import type { MaybeRef } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, unref } from '@vue/composition-api';
import type { Channel } from 'pusher-js';
import { RecipientEvent } from '@getaccept/pusher';
import { pusherClient } from '../pusher';
import { EidEasyService } from './eid-easy.service';
import { EidEasyFailReason } from './types/eid-easy-fail-reason';
import type { EidEasyDoc } from './types/eid-easy-doc';

export const useEidEasy = (recipientPusherChannel: MaybeRef<Channel>) => {
  const error = ref<EidEasyFailReason>(null);

  const prepareLoading = ref(true);
  const prepareEidEasyDoc = async () => {
    prepareLoading.value = true;
    try {
      await EidEasyService.prepareEidEasyDoc();
    } catch (e) {
      error.value = EidEasyFailReason.Unknown;
      prepareLoading.value = false;
    }
  };

  const eidEasyDoc = ref<EidEasyDoc>(null);
  const getDocLoading = ref(false);
  const getEidEasyDoc = async () => {
    getDocLoading.value = true;
    try {
      eidEasyDoc.value = await EidEasyService.getEidEasyDoc();
    } catch (e) {
      error.value = EidEasyFailReason.Unknown;
    } finally {
      getDocLoading.value = false;
    }
  };

  const handlePrepareSuccess = () => {
    getEidEasyDoc();
    prepareLoading.value = false;
  };

  const handlePrepareFail = ({ eidEasyFailReason }: { eidEasyFailReason: EidEasyFailReason }) => {
    prepareLoading.value = false;
    getDocLoading.value = false;
    error.value = eidEasyFailReason;
  };

  const handleSignFail = ({ eidEasyFailReason }: { eidEasyFailReason: EidEasyFailReason }) => {
    error.value = eidEasyFailReason;
  };

  const addListeners = () => {
    pusherClient.listen(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasyPrepareSuccess,
      handlePrepareSuccess
    );
    pusherClient.listen(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasyPrepareFail,
      handlePrepareFail
    );
    pusherClient.listen(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasySignFail,
      handleSignFail
    );
  };

  const removeListeners = () => {
    pusherClient.unbind(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasyPrepareSuccess,
      handlePrepareSuccess
    );
    pusherClient.unbind(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasyPrepareFail,
      handlePrepareFail
    );
    pusherClient.unbind(
      unref(recipientPusherChannel) as Channel,
      RecipientEvent.EidEasySignFail,
      handleSignFail
    );
  };

  const reset = () => {
    eidEasyDoc.value = null;
    error.value = null;
    prepareLoading.value = false;
    getDocLoading.value = false;
  };

  const retry = async () => {
    reset();
    await prepareEidEasyDoc();
  };

  const loading = computed(() => prepareLoading.value || getDocLoading.value);

  onMounted(() => {
    addListeners();
    prepareEidEasyDoc();
  });

  onBeforeUnmount(() => {
    removeListeners();
  });

  return {
    retry,
    error,
    loading,
    eidEasyDoc,
  };
};
